"use client";

import { useState, useEffect, useCallback } from "react";
import { categoryStructure } from "@/lib/categoryStructure";
import {
  getActiveCategoryStructure,
  excludeDeprecatedFurnitureSubcategories,
  activationFromApiResponse,
  type CategoryActivation,
} from "@/lib/activeCategories";
import type { CategoryStructure } from "@/lib/categoryStructure";

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

type Cached = {
  activation: CategoryActivation;
  timestamp: number;
};

let cache: Cached | null = null;

export interface UseActiveCategoryStructureResult {
  activeCategoryStructure: CategoryStructure[];
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

/**
 * Fetches active category/subcategory ids from the public API, builds activation sets,
 * and returns the filtered category structure for platform catalog pickers
 * (sell, market, request-item, near-me prefs, bare CategoryDropdown).
 * Always drops picker-hidden legacy ids. Uses a short client-side cache.
 */
export function useActiveCategoryStructure(): UseActiveCategoryStructureResult {
  const [activation, setActivation] = useState<CategoryActivation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchActive = useCallback(async () => {
    const now = Date.now();
    if (cache && now - cache.timestamp < CACHE_TTL_MS) {
      setActivation(cache.activation);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/categories/active", {
        cache: "no-store",
        headers: { "Cache-Control": "no-cache" },
      });
      if (!res.ok) {
        throw new Error(`Failed to load categories: ${res.status}`);
      }
      const data = (await res.json()) as {
        categories: string[];
        subcategories: string[];
      };
      const next = activationFromApiResponse(data);
      cache = { activation: next, timestamp: Date.now() };
      setActivation(next);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
      setActivation(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      void fetchActive();
    }, 0);
    return () => clearTimeout(timer);
  }, [fetchActive]);

  const activeCategoryStructure =
    activation === null
      ? excludeDeprecatedFurnitureSubcategories(categoryStructure)
      : getActiveCategoryStructure(categoryStructure, activation);

  return {
    activeCategoryStructure,
    isLoading,
    error,
    refresh: fetchActive,
  };
}
